<template>
  <div>
    <div class="container-fluid">
      <div class="row">
        <div class="col-lg-12">
          <div class="d-flex flex-wrap flex-wrap align-items-center justify-content-center">
            <div class="text-center">
              <h4 class="mb-3">상품 추적</h4>
              <p class="mb-0">특정 상품의 키워드별 트래픽 지표 변화를 추적하여 분석합니다.<br>
                상품 URL과 기간을 설정하여 8가지 핵심 지표의 변화를 시각화합니다.
              </p>
                <small class="mb-0 text-primary" style="line-height:0%">
                  활용팁 1. 초기 상품 키워드 세팅 시 경쟁상품 벤치마킹<br>
                  활용팁 2. 초기 상품 등록 후 노출지수 확인<br>
                  활용팁 3. 초기 상품 등록 시 키워드 변경하면서 SEO 최적화<br>
                  활용팁 4. 나의 기존 상품 노출 추적해서 노출 효과 좋은 키워드 찾기<br>
                  활용팁 5. 경쟁사 노출 경로 및 작업 방법 History 추적<br>
                </small>
            </div>
          </div>
        </div>

        <div class="col-lg-12">
          <!-- 상품 추적 설정 -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="card-title">상품 추적 설정</h5>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="productUrl">상품 URL</label>
                    <input
                      type="text"
                      id="productUrl"
                      v-model="productUrl"
                      class="form-control"
                      placeholder="https://www.coupang.com/vp/products/1111111111?vendorItemId=2222222222"
                      :disabled="isTracking"
                    />
                    <small class="form-text text-muted">
                      쿠팡 상품 URL을 입력해주세요 (productId와 vendorItemId가 포함된 URL)
                    </small>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="dateRange">추적 기간</label>
                    <select
                      id="dateRange"
                      v-model="selectedDateRange"
                      class="form-control"
                      :disabled="isTracking"
                      @change="updateDateRange"
                    >
                      <option value="7">최근 7일</option>
                      <option value="30">최근 30일</option>
                      <option value="60">최근 60일</option>
                      <option value="90">최근 90일</option>
                      <option value="180">최근 180일</option>
                      <option value="360">최근 360일</option>
                    </select>
                    <small class="form-text text-muted">
                      {{ dateRangeText }}
                    </small>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-12">
                  <button
                    type="button"
                    class="btn btn-primary"
                    @click="startProductTrace"
                    :disabled="!canStartTrace || isTracking"
                  >
                    <span v-if="isTracking" class="spinner-border spinner-border-sm mr-2" role="status"></span>
                    {{ isTracking ? '추적 중...' : '상품 추적 시작' }}
                  </button>
                  <button
                    type="button"
                    class="btn btn-secondary ml-2"
                    @click="resetData"
                    :disabled="isTracking"
                  >
                    초기화
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 진행 상태 -->
      <div class="row" v-if="isTracking">
        <div class="col-lg-12">
          <div class="card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="mb-1">{{ progressMessage }}</h6>
                  <div class="progress" style="height: 6px;">
                    <div 
                      class="progress-bar" 
                      role="progressbar" 
                      :style="`width: ${progressPercent}%`"
                    ></div>
                  </div>
                </div>
                <div class="text-end">
                  <span class="badge bg-primary">{{ progressPercent.toFixed(0) }}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 상품 정보 -->
      <div v-if="productInfo" class="row mb-4">
        <div class="col-lg-12">
          <div class="card">
            <div class="card-header">
              <h5 class="card-title">추적 상품 정보</h5>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-2">
                  <img v-if="productInfo.imagePath" :src="'http://thumbnail.coupangcdn.com/thumbnails/remote/492x492ex/image/' + productInfo.imagePath" class="img-fluid rounded" alt="상품 이미지">
                </div>
                <div class="col-md-10">
                  <h6>{{ productInfo.productName }}</h6>
                  <p class="text-muted mb-1">Product ID: {{ productInfo.productId }}</p>
                  <p class="text-muted mb-1">Vendor Item ID: {{ productInfo.vendorItemId }}</p>
                  <p class="text-muted mb-0">추적 기간: {{ startDate }} ~ {{ endDate }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 키워드 선택 -->
      <div v-if="trafficData && trafficData.length > 0" class="row mb-4">
        <div class="col-lg-12">
          <div class="card">
            <div class="card-header">
              <h5 class="card-title">키워드 선택</h5>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-4">
                  <div class="form-group">
                    <label for="keywordSelect">분석할 키워드 선택</label>
                    <select
                      id="keywordSelect"
                      v-model="selectedKeyword"
                      class="form-control"
                      @change="onKeywordChange"
                    >
                      <option value="">전체 (기본)</option>
                      <option v-for="keyword in availableKeywords" :key="keyword" :value="keyword">
                        {{ keyword }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="col-md-8">
                  <p class="text-muted mt-2 mb-2">
                    <strong>전체:</strong> 모든 키워드의 개별 차트를 표시합니다.<br>
                    <strong>개별 키워드:</strong> 선택한 키워드의 통합 분석 차트를 표시합니다.
                  </p>

                  <!-- 추가 키워드 수집 -->
                  <div class="mt-3">
                    <label class="form-label">추가 키워드 수집</label>
                    <div class="input-group">
                      <input
                        v-model="additionalKeyword"
                        type="text"
                        class="form-control"
                        placeholder="추가할 키워드 입력"
                        @keyup.enter="addKeywordToTrace"
                        :disabled="isAddingKeyword"
                      />
                      <button
                        @click="addKeywordToTrace"
                        :disabled="!additionalKeyword.trim() || isAddingKeyword || !trafficData || trafficData.length === 0"
                        class="btn btn-success"
                        type="button"
                      >
                        {{ isAddingKeyword ? '수집 중...' : '추가 수집' }}
                      </button>
                    </div>
                    <small class="text-muted">기존 상품의 동일 날짜 범위로 키워드를 추가 수집합니다.</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 개별 키워드 통합 차트 -->
      <div v-if="selectedKeyword && trafficData && trafficData.length > 0"
           :class="['row', { 'd-none': isChartsHidden }]">
        <div class="col-md-12 mb-4">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="card-title mb-0">{{ selectedKeyword }} - 통합 분석</h5>
              <button
                class="btn btn-outline-primary btn-sm mr-1"
                @click="downloadChartData('keyword')"
                title="CSV 다운로드"
              >
                <i class="fas fa-download"></i>CSV
              </button>
            </div>
            <div class="card-body">
              <!-- 로딩 스피너 -->
              <div v-if="isLoadingKeywordChart" class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="sr-only">Loading...</span>
                </div>
                <p class="mt-2 text-muted">차트를 생성하고 있습니다...</p>
              </div>

              <!-- 키워드 차트 -->
              <ApexChart
                v-else-if="showKeywordChart"
                ref="keywordChart"
                element="keyword-chart"
                :chartOption="keywordChartOptions"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- 전체 키워드 총합 차트 -->
      <div v-if="!selectedKeyword && trafficData && trafficData.length > 0"
           :class="['row', { 'd-none': isChartsHidden }]">
        <div class="col-md-12 mb-4">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="card-title mb-3">전체 키워드 총합 분석</h5>
              <div class="d-flex flex-wrap gap-2">
                <button
                  class="btn btn-outline-primary btn-sm mr-1"
                  @click="downloadChartData('summary')"
                  title="전체 키워드 총합 분석 CSV 다운로드"
                >
                  <i class="fas fa-download"></i>전체 총합
                </button>

                <button
                  class="btn btn-outline-primary btn-sm mr-1"
                  @click="downloadChartData('totalImpressions')"
                  title="총 노출수 변화 CSV 다운로드"
                >
                  <i class="fas fa-download"></i>총노출수
                </button>
                <button
                  class="btn btn-outline-secondary btn-sm mr-1"
                  @click="downloadChartData('organicImpressions')"
                  title="자연 노출수 변화 CSV 다운로드"
                >
                  <i class="fas fa-download"></i>자연노출수
                </button>
                <button
                  class="btn btn-outline-primary btn-sm mr-1"
                  @click="downloadChartData('adImpressions')"
                  title="광고 노출수 변화 CSV 다운로드"
                >
                  <i class="fas fa-download"></i>광고노출수
                </button>
                <button
                  class="btn btn-outline-secondary btn-sm mr-1"
                  @click="downloadChartData('clicks')"
                  title="클릭수 변화 CSV 다운로드"
                >
                  <i class="fas fa-download"></i>클릭수
                </button>
                <button
                  class="btn btn-outline-primary btn-sm mr-1"
                  @click="downloadChartData('ctr')"
                  title="CTR 변화 CSV 다운로드"
                >
                  <i class="fas fa-download"></i>클릭율
                </button>

                <button
                  class="btn btn-outline-primary btn-sm mr-1"
                  @click="downloadChartData('avgCpc')"
                  title="평균단가 변화 CSV 다운로드"
                >
                  <i class="fas fa-download"></i>평균단가
                </button>
                <button
                  class="btn btn-outline-secondary btn-sm mr-1"
                  @click="downloadChartData('searchVolume')"
                  title="검색량 변화 CSV 다운로드"
                >
                  <i class="fas fa-download"></i>검색량
                </button>
              </div>
            </div>
            <div class="card-body">
              <ApexChart
                v-if="showSummaryChart"
                ref="summaryChart"
                element="summary-chart"
                :chartOption="summaryChartOptions"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- 상세 차트 보기 토글 버튼 -->
      <div v-if="!selectedKeyword && trafficData && trafficData.length > 0"
           :class="['row', 'mb-4', { 'd-none': isChartsHidden }]">
        <div class="col-12 text-center">
          <button
            class="btn btn-outline-secondary"
            type="button"
            @click="toggleDetailCharts"
            :aria-expanded="showDetailCharts"
          >
            <i :class="showDetailCharts ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="me-2"></i>
            {{ showDetailCharts ? '상세 차트 숨기기' : '상세 차트 보기' }}
          </button>
        </div>
      </div>

      <!-- 상세 차트 섹션 -->
      <div v-if="!selectedKeyword && trafficData && trafficData.length > 0 && showDetailCharts"
           :class="['row', { 'd-none': isChartsHidden }]">
        <!-- 총 노출수 변화 -->
        <div class="col-md-12 mb-0">
          <div class="card">
            <div class="card-header">
              <h5 class="card-title mb-0">총 노출수 변화</h5>
            </div>
            <div class="card-body">
              <ApexChart
                v-if="showTotalImpressionChart"
                ref="totalImpressionChart"
                element="total-impression-chart"
                :chartOption="totalImpressionChartOptions"
              />
            </div>
          </div>
        </div>

        <!-- 자연노출수 변화 -->
        <div class="col-md-12 mb-0">
          <div class="card">
            <div class="card-header">
              <h5 class="card-title mb-0">자연노출수 변화</h5>
            </div>
            <div class="card-body">
              <ApexChart
                v-if="showOrganicImpressionChart"
                ref="organicImpressionChart"
                element="organic-impression-chart"
                :chartOption="organicImpressionChartOptions"
              />
            </div>
          </div>
        </div>

        <!-- 광고노출수 변화 -->
        <div class="col-md-12 mb-0">
          <div class="card">
            <div class="card-header">
              <h5 class="card-title mb-0">광고노출수 변화</h5>
            </div>
            <div class="card-body">
              <ApexChart
                v-if="showAdImpressionChart"
                ref="adImpressionChart"
                element="ad-impression-chart"
                :chartOption="adImpressionChartOptions"
              />
            </div>
          </div>
        </div>

        <!-- 클릭수 변화 -->
        <div class="col-md-12 mb-0">
          <div class="card">
            <div class="card-header">
              <h5 class="card-title mb-0">클릭수 변화</h5>
            </div>
            <div class="card-body">
              <ApexChart
                v-if="showClickChart"
                ref="clickChart"
                element="click-chart"
                :chartOption="clickChartOptions"
              />
            </div>
          </div>
        </div>

        <!-- CTR 변화 -->
        <div class="col-md-12 mb-0">
          <div class="card">
            <div class="card-header">
              <h5 class="card-title mb-0">CTR 변화</h5>
            </div>
            <div class="card-body">
              <ApexChart
                v-if="showCtrChart"
                ref="ctrChart"
                element="ctr-chart"
                :chartOption="ctrChartOptions"
              />
            </div>
          </div>
        </div>



        <!-- 키워드 평균단가 변화 -->
        <div class="col-md-12 mb-0">
          <div class="card">
            <div class="card-header">
              <h5 class="card-title mb-0">키워드 평균단가 변화</h5>
            </div>
            <div class="card-body">
              <ApexChart
                v-if="showAvgCpcChart"
                ref="avgCpcChart"
                element="avg-cpc-chart"
                :chartOption="avgCpcChartOptions"
              />
            </div>
          </div>
        </div>

        <!-- 키워드검색량 변화 -->
        <div class="col-md-12 mb-0">
          <div class="card">
            <div class="card-header">
              <h5 class="card-title mb-0">키워드검색량 변화</h5>
            </div>
            <div class="card-body">
              <ApexChart
                v-if="showSearchVolumeChart"
                ref="searchVolumeChart"
                element="search-volume-chart"
                :chartOption="searchVolumeChartOptions"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- 데이터가 없을 때 -->
      <div v-if="!isTracking && (!trafficData || trafficData.length === 0)" class="row">
        <div class="col-lg-12">
          <div class="card">
            <div class="card-body text-center py-5">
              <i class="las la-chart-line" style="font-size: 4rem; color: #ccc;"></i>
              <h5 class="mt-3 text-muted">추적할 상품을 설정해주세요</h5>
              <p class="text-muted">상품 URL과 기간을 입력하고 '상품 추적 시작' 버튼을 클릭하세요.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApexChart from '@/components/charts/ApexChart.vue'

export default {
  name: 'ProductTrace',
  components: {
    ApexChart
  },
  data() {
    return {
      // 입력 데이터
      productUrl: '',
      selectedDateRange: '30', // 기본값: 최근 30일
      startDate: '',
      endDate: '',

      // 상태 관리
      isTracking: false,
      progressMessage: '',
      progressPercent: 0,
      trackingTimeout: null,

      // 상품 정보
      productInfo: null,

      // 트래픽 데이터
      trafficData: [],

      // 키워드 선택
      selectedKeyword: '',
      availableKeywords: [],

      // 추가 키워드 수집
      additionalKeyword: '',
      isAddingKeyword: false,

      // 차트 업데이트 상태
      isChartsHidden: false,
      isLoadingKeywordChart: false,

      // 차트 표시 상태
      showTotalImpressionChart: false,
      showOrganicImpressionChart: false,
      showAdImpressionChart: false,
      showClickChart: false,
      showCtrChart: false,

      showAvgCpcChart: false,
      showSearchVolumeChart: false,
      showKeywordChart: false,

      showSummaryChart: false,
      showDetailCharts: false,
      
      // 차트 옵션들
      totalImpressionChartOptions: {},
      organicImpressionChartOptions: {},
      adImpressionChartOptions: {},
      clickChartOptions: {},
      ctrChartOptions: {},

      avgCpcChartOptions: {},
      searchVolumeChartOptions: {},
      keywordChartOptions: {},

      summaryChartOptions: {}
    }
  },
  computed: {
    canStartTrace() {
      return this.productUrl && this.selectedDateRange && this.isValidProductUrl
    },
    isValidProductUrl() {
      if (!this.productUrl) return false
      const urlPattern = /coupang\.com\/vp\/products\/(\d+).*[?&]vendorItemId=(\d+)/
      return urlPattern.test(this.productUrl)
    },
    dateRangeText() {
      if (!this.selectedDateRange) return ''
      const today = new Date()
      const daysAgo = parseInt(this.selectedDateRange)
      const startDate = new Date(today.getTime() - (daysAgo - 1) * 24 * 60 * 60 * 1000)

      const formatDate = (date) => {
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const weekdays = ['일', '월', '화', '수', '목', '금', '토']
        const weekday = weekdays[date.getDay()]
        return `${month}.${day} (${weekday})`
      }

      return `${formatDate(startDate)} ~ ${formatDate(today)}`
    }
  },
  created() {
    // 기본 날짜 범위 설정
    this.updateDateRange()

    // 메시지 리스너 등록
    window.addEventListener('message', this.handleMessage)
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleMessage)
    this.clearTrackingTimeout()
  },
  methods: {
    // 날짜 범위 업데이트
    updateDateRange() {
      const today = new Date()
      const daysAgo = parseInt(this.selectedDateRange)
      const startDate = new Date(today.getTime() - (daysAgo - 1) * 24 * 60 * 60 * 1000)

      this.endDate = today.toISOString().split('T')[0]
      this.startDate = startDate.toISOString().split('T')[0]
    },

    // 상품 추적 시작
    startProductTrace() {
      if (!this.canStartTrace) {
        this.$toast.error('상품 URL과 기간을 올바르게 입력해주세요.')
        return
      }

      const urlMatch = this.productUrl.match(/coupang\.com\/vp\/products\/(\d+).*[?&]vendorItemId=(\d+)/)
      if (!urlMatch) {
        this.$toast.error('올바른 쿠팡 상품 URL을 입력해주세요.\n예: https://www.coupang.com/vp/products/1111111111?vendorItemId=2222222222')
        return
      }

      // 날짜 범위 업데이트
      this.updateDateRange()

      const productId = urlMatch[1]
      const vendorItemId = urlMatch[2]

      // URL에서 itemId 파라미터 추출 (있으면 사용)
      const itemIdMatch = this.productUrl.match(/[?&]itemId=(\d+)/)
      const itemId = itemIdMatch ? itemIdMatch[1] : null

      this.isTracking = true
      this.progressMessage = '상품 정보 수집 중...'
      this.progressPercent = 10

      // 기존 데이터 초기화
      this.resetData()

      // Extension에 상품 추적 요청 (새로운 방식)
      window.postMessage({
        type: 'startProductTraceV2',
        productId: productId,
        vendorItemId: vendorItemId,
        itemId: itemId,  // URL에서 추출한 itemId (없으면 null)
        dateRange: this.selectedDateRange,
        startDate: this.startDate,
        endDate: this.endDate
      }, "*")

      // 타임아웃 설정 (5분)
      this.trackingTimeout = setTimeout(() => {
        if (this.isTracking) {
          this.handleTraceError({ message: '상품 추적이 시간 초과되었습니다. 다시 시도해주세요.' })
        }
      }, 300000) // 5분
    },
    
    // 메시지 핸들러
    handleMessage(event) {
      if (event.data.res === 'productTraceComplete') {
        this.handleTraceComplete(event.data)
      } else if (event.data.res === 'productTraceProgress') {
        this.handleTraceProgress(event.data)
      } else if (event.data.res === 'productTraceError') {
        this.handleTraceError(event.data)
      } else if (event.data.type === 'addKeywordToTraceComplete') {
        console.log('🔍 [Frontend] addKeywordToTraceComplete 메시지 받음:', event.data)
        this.handleAddKeywordResponse(event.data)
      }
    },
    
    // 추적 완료 처리
    handleTraceComplete(data) {
      this.clearTrackingTimeout()
      this.isTracking = false
      this.progressPercent = 100
      this.progressMessage = '추적 완료!'

      if (data.productInfo) {
        this.productInfo = data.productInfo
      }

      if (data.trafficData && data.trafficData.length > 0) {
        this.trafficData = data.trafficData
        this.extractAvailableKeywords()
        this.createCharts()

        // IndexedDB에 데이터 저장
        // await this.saveDataToIndexedDB()

        console.log(`✅ 상품 추적이 완료되었습니다. ${data.trafficData.length}개의 키워드 데이터를 수집했습니다.`)
        alert(`상품 추적이 완료되었습니다. ${data.trafficData.length}개의 키워드 데이터를 수집했습니다.`)
      } else {
        console.warn('⚠️ 추적은 완료되었지만 키워드 데이터가 없습니다.')
        alert('추적은 완료되었지만 키워드 데이터가 없습니다.')
      }
    },

    // 진행 상황 업데이트
    handleTraceProgress(data) {
      if (data.message) {
        this.progressMessage = data.message
      }
      if (data.percent !== undefined) {
        this.progressPercent = Math.min(Math.max(data.percent, 0), 100)
      }
    },

    // 에러 처리
    handleTraceError(data) {
      this.clearTrackingTimeout()
      this.isTracking = false
      this.progressPercent = 0
      this.progressMessage = ''

      const errorMessage = data.message || '상품 추적 중 오류가 발생했습니다.'
      console.error('[상품 추적 에러]', errorMessage)
      alert(errorMessage)

      // 상세 에러 로그
      console.error('[상품 추적 에러]', data)
    },

    // 타임아웃 정리
    clearTrackingTimeout() {
      if (this.trackingTimeout) {
        clearTimeout(this.trackingTimeout)
        this.trackingTimeout = null
      }
    },
    
    // 데이터 초기화
    resetData() {
      this.clearTrackingTimeout()
      this.productInfo = null
      this.trafficData = []
      this.selectedKeyword = ''
      this.availableKeywords = []
      this.progressMessage = ''
      this.progressPercent = 0
      this.hideAllCharts()
    },
    
    // 모든 차트 숨기기
    hideAllCharts() {
      this.showTotalImpressionChart = false
      this.showOrganicImpressionChart = false
      this.showAdImpressionChart = false
      this.showClickChart = false
      this.showCtrChart = false

      this.showAvgCpcChart = false
      this.showSearchVolumeChart = false
      this.showKeywordChart = false

      this.showSummaryChart = false
    },
    
    // 키워드 목록 추출
    extractAvailableKeywords() {
      const keywords = new Set()
      this.trafficData.forEach(item => {
        if (item.keywordDetail && item.keywordDetail.keyword) {
          keywords.add(item.keywordDetail.keyword)
        }
      })
      this.availableKeywords = Array.from(keywords).sort()
    },

    // 키워드 변경 처리
    onKeywordChange() {
      this.hideAllCharts()

      if (this.selectedKeyword) {
        this.isLoadingKeywordChart = true
        this.$nextTick(() => {
          setTimeout(() => {
            this.createKeywordChart()
            this.isLoadingKeywordChart = false
          }, 100)
        })
      } else {
        this.isChartsHidden = true
        this.$nextTick(() => {
          setTimeout(() => {
            this.createCharts()
            this.isChartsHidden = false
          }, 100)
        })
      }
    },

    // 차트 생성
    createCharts() {
      this.$nextTick(() => {
        this.createSummaryChart()
        this.createTotalImpressionChart()
        this.createOrganicImpressionChart()
        this.createAdImpressionChart()
        this.createClickChart()
        this.createCtrChart()

        this.createAvgCpcChart()
        this.createSearchVolumeChart()
      })
    },

    // Collapse 토글 함수
    toggleDetailCharts() {
      this.showDetailCharts = !this.showDetailCharts
    },

    // CSV 다운로드 함수
    downloadChartData(chartType) {
      try {
        let csvData = ''
        let filename = ''

        switch(chartType) {
          case 'summary':
            csvData = this.generateSummaryCSV()
            filename = '전체키워드_총합분석.csv'
            break
          case 'keyword':
            csvData = this.generateKeywordCSV()
            filename = `${this.selectedKeyword}_통합분석.csv`
            break
          case 'totalImpressions':
            csvData = this.generateIndividualChartCSV('totalImpressions')
            filename = '총노출수_변화.csv'
            break
          case 'organicImpressions':
            csvData = this.generateIndividualChartCSV('organicImpressions')
            filename = '자연노출수_변화.csv'
            break
          case 'adImpressions':
            csvData = this.generateIndividualChartCSV('adImpressions')
            filename = '광고노출수_변화.csv'
            break
          case 'clicks':
            csvData = this.generateIndividualChartCSV('clicks')
            filename = '클릭수_변화.csv'
            break
          case 'ctr':
            csvData = this.generateIndividualChartCSV('ctr')
            filename = 'CTR_변화.csv'
            break

          case 'avgCpc':
            csvData = this.generateIndividualChartCSV('avgCpc')
            filename = '평균단가_변화.csv'
            break
          case 'searchVolume':
            csvData = this.generateIndividualChartCSV('searchVolume')
            filename = '검색량_변화.csv'
            break
          default:
            console.warn('Unknown chart type:', chartType)
            return
        }

        if (csvData) {
          this.downloadCSV(csvData, filename)
        }
      } catch (error) {
        console.error('CSV 다운로드 오류:', error)
        alert('CSV 다운로드 중 오류가 발생했습니다.')
      }
    },

    // CSV 파일 다운로드 실행
    downloadCSV(csvContent, filename) {
      const BOM = '\uFEFF' // UTF-8 BOM for Excel compatibility
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', filename)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      }
    },



    // 총합 차트 CSV 생성
    generateSummaryCSV() {
      if (!this.summaryChartOptions.series || this.summaryChartOptions.series.length === 0) {
        return ''
      }

      const dateRange = this.generateDateRange()
      let csv = '날짜,총 노출수,자연 노출수,광고 노출수,클릭수\n'

      dateRange.forEach(dateStr => {
        const timestamp = new Date(dateStr).getTime()
        csv += dateStr

        this.summaryChartOptions.series.forEach(series => {
          const dataPoint = series.data.find(point => point[0] === timestamp)
          const value = dataPoint ? dataPoint[1] : 0
          csv += ',' + value
        })
        csv += '\n'
      })

      return csv
    },

    // 개별 키워드 차트 CSV 생성
    generateKeywordCSV() {
      if (!this.keywordChartOptions.series || this.keywordChartOptions.series.length === 0) {
        return ''
      }

      const dateRange = this.generateDateRange()
      let csv = '날짜,자연노출수,광고노출수,클릭수\n'

      dateRange.forEach(dateStr => {
        const timestamp = new Date(dateStr).getTime()
        csv += dateStr

        this.keywordChartOptions.series.forEach(series => {
          const dataPoint = series.data.find(point => point[0] === timestamp)
          const value = dataPoint ? dataPoint[1] : 0
          csv += ',' + value
        })
        csv += '\n'
      })

      return csv
    },

    // 개별 차트 CSV 생성
    generateIndividualChartCSV(metric) {
      const series = this.generateTimeSeriesData(metric)
      if (!series || series.length === 0) {
        return ''
      }

      const dateRange = this.generateDateRange()
      let csv = '날짜'

      // 헤더 생성
      series.forEach(seriesItem => {
        csv += ',' + seriesItem.name
      })
      csv += '\n'

      // 데이터 생성
      dateRange.forEach(dateStr => {
        const timestamp = new Date(dateStr).getTime()
        csv += dateStr

        series.forEach(seriesItem => {
          const dataPoint = seriesItem.data.find(point => point[0] === timestamp)
          const value = dataPoint ? dataPoint[1] : 0
          csv += ',' + value
        })
        csv += '\n'
      })

      return csv
    },





    // 전체 키워드 총합 차트 생성
    createSummaryChart() {
      if (!this.trafficData || this.trafficData.length === 0) {
        return
      }

      // 전체 날짜 범위 생성
      const dateRange = this.generateDateRange()

      // 날짜별 총합 데이터 초기화
      const summaryData = {}
      dateRange.forEach(dateStr => {
        summaryData[dateStr] = {
          totalImpressions: 0,
          organicImpressions: 0,
          adImpressions: 0,
          clicks: 0
        }
      })

      // 모든 키워드의 데이터를 날짜별로 합산
      this.trafficData.forEach(item => {
        if (!item.keywordDetail || !item.keywordMetric) return

        if (item.dailyData && Array.isArray(item.dailyData)) {
          item.dailyData.forEach(daily => {
            const dateKey = daily.date
            if (summaryData[dateKey]) {
              summaryData[dateKey].totalImpressions += this.normalizeValue(daily.impression)
              summaryData[dateKey].organicImpressions += this.normalizeValue(daily.organicImpression)
              summaryData[dateKey].adImpressions += this.normalizeValue(daily.adImpression)
              summaryData[dateKey].clicks += this.normalizeValue(daily.srpClick)
            }
          })
        } else {
          // dailyData가 없는 경우 오늘 날짜에 추가
          const today = new Date().toISOString().split('T')[0]
          if (summaryData[today]) {
            summaryData[today].totalImpressions += this.normalizeValue(item.keywordMetric.impression)
            summaryData[today].organicImpressions += this.normalizeValue(item.keywordMetric.organicImpression)
            summaryData[today].adImpressions += this.normalizeValue(item.keywordMetric.adImpression)
            summaryData[today].clicks += this.normalizeValue(item.keywordMetric.srpClick)
          }
        }
      })

      // 시리즈 데이터 생성
      const series = [
        {
          name: '총 노출수',
          type: 'line',
          data: dateRange.map(dateStr => {
            const timestamp = new Date(dateStr).getTime()
            const value = this.normalizeValue(summaryData[dateStr].totalImpressions)
            return [timestamp, value]
          }).filter(point => this.isValidDataPoint(point[0]) && typeof point[1] === 'number' && !isNaN(point[1]))
        },
        {
          name: '자연 노출수',
          type: 'line',
          data: dateRange.map(dateStr => {
            const timestamp = new Date(dateStr).getTime()
            const value = this.normalizeValue(summaryData[dateStr].organicImpressions)
            return [timestamp, value]
          }).filter(point => this.isValidDataPoint(point[0]) && typeof point[1] === 'number' && !isNaN(point[1]))
        },
        {
          name: '광고 노출수',
          type: 'line',
          data: dateRange.map(dateStr => {
            const timestamp = new Date(dateStr).getTime()
            const value = this.normalizeValue(summaryData[dateStr].adImpressions)
            return [timestamp, value]
          }).filter(point => this.isValidDataPoint(point[0]) && typeof point[1] === 'number' && !isNaN(point[1]))
        },
        {
          name: '클릭수',
          type: 'line',
          data: dateRange.map(dateStr => {
            const timestamp = new Date(dateStr).getTime()
            const value = this.normalizeValue(summaryData[dateStr].clicks)
            return [timestamp, value]
          }).filter(point => this.isValidDataPoint(point[0]) && typeof point[1] === 'number' && !isNaN(point[1]))
        }
      ]

      this.summaryChartOptions = {
        series: series,
        chart: {
          type: 'line',
          height: 500,
          zoom: {
            enabled: true
          }
        },
        stroke: {
          width: 3,
          curve: 'smooth'
        },
        xaxis: {
          type: 'datetime',
          min: new Date(this.startDate).getTime(),
          max: new Date(this.endDate).getTime(),
          title: {
            text: '날짜'
          }
        },
        yaxis: {
          title: {
            text: '수량'
          },
          labels: {
            formatter: function(val) {
              return val ? val.toLocaleString() : '0'
            }
          }
        },
        tooltip: {
          shared: true,
          intersect: false,
          followCursor: true,
          fixed: {
            enabled: false
          },
          x: {
            format: 'yyyy-MM-dd'
          },
          y: {
            formatter: function(val, { seriesIndex }) {
              const labels = ['총 노출', '자연 노출', '광고 노출', '클릭']
              return val ? val.toLocaleString() + '회 ' + labels[seriesIndex] : '0회'
            }
          }
        },
        legend: {
          position: 'bottom'
        },
        colors: ['#008FFB', '#00E396', '#FF4560', '#775DD0']
      }

      this.showSummaryChart = true
    },

    // 총 노출수 차트 생성
    createTotalImpressionChart() {
      const series = this.generateTimeSeriesData('totalImpressions')
      const baseOptions = this.getBaseChartOptions(
        '노출수',
        function(val) { return val ? val.toLocaleString() : '0' },
        function(val) { return val ? val.toLocaleString() : '0' }
      )

      this.totalImpressionChartOptions = {
        ...baseOptions,
        series: series
      }

      this.showTotalImpressionChart = true
    },

    // 자연노출수 차트 생성
    createOrganicImpressionChart() {
      const series = this.generateTimeSeriesData('organicImpressions')
      const baseOptions = this.getBaseChartOptions(
        '자연노출수',
        function(val) { return val ? val.toLocaleString() : '0' },
        function(val) { return val ? val.toLocaleString() : '0' }
      )

      this.organicImpressionChartOptions = {
        ...baseOptions,
        series: series
      }

      this.showOrganicImpressionChart = true
    },

    // 광고노출수 차트 생성
    createAdImpressionChart() {
      const series = this.generateTimeSeriesData('adImpressions')
      const baseOptions = this.getBaseChartOptions(
        '광고노출수',
        function(val) { return val ? val.toLocaleString() : '0' },
        function(val) { return val ? val.toLocaleString() : '0' }
      )

      this.adImpressionChartOptions = {
        ...baseOptions,
        series: series
      }

      this.showAdImpressionChart = true
    },

    // 클릭수 차트 생성
    createClickChart() {
      const series = this.generateTimeSeriesData('clicks')
      const baseOptions = this.getBaseChartOptions(
        '클릭수',
        function(val) { return val ? val.toLocaleString() : '0' },
        function(val) { return val ? val.toLocaleString() : '0' }
      )

      this.clickChartOptions = {
        ...baseOptions,
        series: series
      }

      this.showClickChart = true
    },

    // CTR 차트 생성
    createCtrChart() {
      const series = this.generateTimeSeriesData('ctr')
      const baseOptions = this.getBaseChartOptions(
        'CTR (%)',
        function(val) { return val ? val.toFixed(2) + '%' : '0%' },
        function(val) { return val ? val.toFixed(2) + '%' : '0%' }
      )

      this.ctrChartOptions = {
        ...baseOptions,
        series: series
      }

      this.showCtrChart = true
    },



    // 키워드 평균단가 차트 생성
    createAvgCpcChart() {
      const series = this.generateTimeSeriesData('avgCpc')
      const baseOptions = this.getBaseChartOptions(
        '평균단가 (원)',
        function(val) { return val ? '₩' + val.toLocaleString() : '₩0' },
        function(val) { return val ? '₩' + val.toLocaleString() : '₩0' }
      )

      this.avgCpcChartOptions = {
        ...baseOptions,
        series: series
      }

      this.showAvgCpcChart = true
    },

    // 키워드검색량 차트 생성
    createSearchVolumeChart() {
      const series = this.generateTimeSeriesData('searchVolume')
      const baseOptions = this.getBaseChartOptions(
        '검색량',
        function(val) { return val ? val.toLocaleString() : '0' },
        function(val) { return val ? val.toLocaleString() : '0' }
      )

      this.searchVolumeChartOptions = {
        ...baseOptions,
        series: series
      }

      this.showSearchVolumeChart = true
    },

    // 개별 키워드 통합 차트 생성
    createKeywordChart() {
      console.log('🔍 [createKeywordChart] 선택된 키워드:', this.selectedKeyword)
      console.log('🔍 [createKeywordChart] 전체 trafficData:', this.trafficData)

      // 새로운 구조와 기존 구조 모두 지원
      const keywordDataList = this.trafficData.filter(item => {
        const keyword = (item.keywordDetail && item.keywordDetail.keyword) || item.keyword
        return keyword === this.selectedKeyword
      })

      console.log('🔍 [createKeywordChart] 필터된 키워드 데이터:', keywordDataList)

      if (!keywordDataList || keywordDataList.length === 0) {
        console.warn('선택한 키워드의 데이터를 찾을 수 없습니다.')
        return
      }

      // 전체 날짜 범위 생성
      const dateRange = this.generateDateRange()

      // 날짜별 데이터 준비 (모든 날짜를 0으로 초기화)
      const dailyDataMap = {}
      dateRange.forEach(dateStr => {
        dailyDataMap[dateStr] = {
          organicImpression: 0,
          adImpression: 0,
          clicks: 0
        }
      })

      // 실제 데이터로 덮어쓰기
      keywordDataList.forEach(keywordData => {
        // 새로운 구조 (직접 속성): 추가 키워드 데이터
        if (keywordData.date && keywordData.keyword) {
          const dateKey = keywordData.date
          if (dailyDataMap[dateKey]) {
            dailyDataMap[dateKey] = {
              organicImpression: keywordData.organicImpression || 0,
              adImpression: keywordData.adImpression || 0,
              clicks: keywordData.srpClick || 0
            }
          }
        }
        // 기존 구조 (dailyData 배열): 초기 상품 추적 데이터
        else if (keywordData.dailyData && Array.isArray(keywordData.dailyData)) {
          keywordData.dailyData.forEach(daily => {
            const dateKey = daily.date
            if (dailyDataMap[dateKey]) {
              dailyDataMap[dateKey] = {
                organicImpression: daily.organicImpression || 0,
                adImpression: daily.adImpression || 0,
                clicks: daily.srpClick || 0
              }
            }
          })
        }
        // keywordMetric이 있는 경우 (기존 구조)
        else if (keywordData.keywordMetric) {
          const today = new Date().toISOString().split('T')[0]
          if (dailyDataMap[today]) {
            dailyDataMap[today] = {
              organicImpression: keywordData.keywordMetric.organicImpression || 0,
              adImpression: keywordData.keywordMetric.adImpression || 0,
              clicks: keywordData.keywordMetric.srpClick || 0
            }
          }
        }
      })

      // 차트 데이터 생성
      const chartData = dateRange.map(dateStr => {
        const timestamp = new Date(dateStr).getTime()
        return {
          x: timestamp,
          organicImpression: dailyDataMap[dateStr].organicImpression,
          adImpression: dailyDataMap[dateStr].adImpression,
          clicks: dailyDataMap[dateStr].clicks
        }
      })

      // 시리즈 데이터 준비
      const series = [
        {
          name: '자연노출수',
          type: 'column',
          data: chartData.map(item => [item.x, this.normalizeValue(item.organicImpression)]).filter(point =>
            this.isValidDataPoint(point[0])
          )
        },
        {
          name: '광고노출수',
          type: 'column',
          data: chartData.map(item => [item.x, this.normalizeValue(item.adImpression)]).filter(point =>
            this.isValidDataPoint(point[0])
          )
        },
        {
          name: '클릭수',
          type: 'line',
          data: chartData.map(item => [item.x, this.normalizeValue(item.clicks)]).filter(point =>
            this.isValidDataPoint(point[0])
          )
        }
      ]

      this.keywordChartOptions = {
        series: series,
        chart: {
          type: 'line',
          height: 400,
          stacked: true,
          zoom: {
            enabled: true
          },
          toolbar: {
            show: true,
            export: {
              csv: {
                filename: undefined,
                columnDelimiter: ',',
                headerCategory: '날짜',
                headerValue: '값',
                categoryFormatter: function(timestamp) {
                  try {
                    if (!timestamp || timestamp === null || timestamp === undefined) {
                      return ''
                    }
                    const date = new Date(Number(timestamp))
                    if (isNaN(date.getTime())) {
                      return ''
                    }
                    return date.toLocaleDateString('ko-KR')
                  } catch (e) {
                    console.warn('Date formatting error:', e)
                    return ''
                  }
                },
                valueFormatter: function(value) {
                  try {
                    if (value === null || value === undefined || isNaN(value)) {
                      return '0'
                    }
                    return String(value)
                  } catch (e) {
                    return '0'
                  }
                }
              }
            }
          }
        },
        plotOptions: {
          bar: {
            columnWidth: '50%'
          }
        },
        stroke: {
          width: [0, 0, 3],
          curve: 'smooth'
        },
        xaxis: {
          type: 'datetime',
          min: new Date(this.startDate).getTime(),
          max: new Date(this.endDate).getTime(),
          title: {
            text: '날짜'
          }
        },
        yaxis: [
          {
            title: {
              text: '노출수'
            },
            labels: {
              formatter: function(val) {
                return val ? val.toLocaleString() : '0'
              }
            }
          },
          {
            opposite: true,
            title: {
              text: '클릭수'
            },
            labels: {
              formatter: function(val) {
                return val ? val.toLocaleString() : '0'
              }
            }
          }
        ],
        tooltip: {
          shared: true,
          intersect: false,
          followCursor: true,
          fixed: {
            enabled: false
          },
          x: {
            format: 'yyyy-MM-dd'
          },
          y: {
            formatter: function(val, { seriesIndex }) {
              if (seriesIndex === 2) { // 클릭수
                return val ? val.toLocaleString() + '회' : '0회'
              } else { // 노출수
                return val ? val.toLocaleString() + '회' : '0회'
              }
            }
          }
        },
        legend: {
          position: 'bottom'
        },
        colors: ['#00E396', '#FF4560', '#008FFB']
      }

      this.showKeywordChart = true
    },

    // 공통 차트 옵션 생성 헬퍼 메서드
    getBaseChartOptions(yAxisTitle, yAxisFormatter, tooltipFormatter) {
      return {
        chart: {
          type: 'line',
          height: 700,
          zoom: {
            enabled: true
          },
          toolbar: {
            show: true
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          curve: 'smooth',
          width: 2
        },
        xaxis: {
          type: 'datetime',
          min: new Date(this.startDate).getTime(),
          max: new Date(this.endDate).getTime(),
          title: {
            text: '날짜'
          }
        },
        yaxis: {
          title: {
            text: yAxisTitle
          },
          labels: {
            formatter: yAxisFormatter
          }
        },
        tooltip: {
          shared: true,
          intersect: false,
          followCursor: true,
          fixed: {
            enabled: false
          },
          x: {
            format: 'yyyy-MM-dd'
          },
          y: {
            formatter: tooltipFormatter
          }
        },
        legend: {
          position: 'bottom'
        }
      }
    },

    // 시계열 데이터 생성 헬퍼 메서드
    generateTimeSeriesData(metric) {
      if (!this.trafficData || this.trafficData.length === 0) {
        return []
      }

      // 전체 날짜 범위 생성 (startDate부터 endDate까지)
      const dateRange = this.generateDateRange()

      // 키워드별로 그룹화
      const keywordGroups = {}
      const keywordDataMap = {}

      // 먼저 모든 키워드의 실제 데이터를 수집
      this.trafficData.forEach(item => {
        let keyword = '알 수 없음'

        // 키워드 추출 (기존 구조와 새로운 구조 모두 지원)
        if (item.keywordDetail && item.keywordDetail.keyword) {
          keyword = item.keywordDetail.keyword
        } else if (item.keyword) {
          keyword = item.keyword
        } else {
          return // 키워드가 없으면 스킵
        }

        if (!keywordDataMap[keyword]) {
          keywordDataMap[keyword] = {}
        }

        // 새로운 구조 (직접 속성): 추가 키워드 데이터
        if (item.date && item.keyword) {
          console.log('🔍 [generateTimeSeriesData] 새로운 구조 데이터 처리:', keyword, item.date, metric)
          const dateKey = item.date
          let value = 0

          // 지표별로 값 추출
          switch(metric) {
            case 'totalImpressions':
              value = item.impression || 0
              break
            case 'organicImpressions':
              value = item.organicImpression || 0
              break
            case 'adImpressions':
              value = item.adImpression || 0
              break
            case 'clicks':
              value = item.srpClick || 0
              break
            case 'ctr':
              value = item.ctr || 0
              break
            case 'avgCpc':
              value = item.avgCpc || 0
              break
            case 'searchVolume':
              value = item.searchVolume || 0
              break
            default:
              value = 0
          }

          keywordDataMap[keyword][dateKey] = value
          console.log('🔍 [generateTimeSeriesData] 새로운 데이터 저장:', keyword, dateKey, metric, value)
        }
        // 기존 구조 (dailyData 배열): 초기 상품 추적 데이터
        else if (item.dailyData && Array.isArray(item.dailyData)) {
          item.dailyData.forEach(daily => {
            const dateKey = daily.date
            let value = 0

            // 지표별로 값 추출
            switch(metric) {
              case 'totalImpressions':
                value = daily.impression || 0
                break
              case 'organicImpressions':
                value = daily.organicImpression || 0
                break
              case 'adImpressions':
                value = daily.adImpression || 0
                break
              case 'clicks':
                value = daily.srpClick || 0
                break
              case 'ctr':
                value = daily.ctr || 0
                break

              case 'avgCpc':
                value = daily.avgCpc || 0
                break
              case 'searchVolume':
                value = daily.searchVolume || 0
                break
              default:
                value = 0
            }

            keywordDataMap[keyword][dateKey] = value
          })
        } else if (item.keywordMetric) {
          // dailyData가 없는 경우 keywordMetric에서 단일 값 사용 (기존 구조)
          const today = new Date().toISOString().split('T')[0]
          let value = 0

          switch(metric) {
            case 'totalImpressions':
              value = item.keywordMetric.impression || 0
              break
            case 'organicImpressions':
              value = item.keywordMetric.organicImpression || 0
              break
            case 'adImpressions':
              value = item.keywordMetric.adImpression || 0
              break
            case 'clicks':
              value = item.keywordMetric.srpClick || 0
              break
            case 'ctr':
              value = item.keywordMetric.ctr || 0
              break
            case 'avgCpc':
              value = (item.keywordDetail && item.keywordDetail.avgPrice) || 0
              break
            case 'searchVolume':
              value = item.keywordMetric.searchVolume || 0
              break
            default:
              value = 0
          }

          keywordDataMap[keyword][today] = value
        }
      })

      // 모든 키워드에 대해 전체 날짜 범위의 데이터를 생성 (없는 날짜는 0으로 채움)
      Object.keys(keywordDataMap).forEach(keyword => {
        keywordGroups[keyword] = []

        dateRange.forEach(dateStr => {
          const timestamp = new Date(dateStr).getTime()
          const value = keywordDataMap[keyword][dateStr] || 0

          // 데이터 검증 후 추가 (값은 정규화)
          if (this.isValidDataPoint(timestamp)) {
            const normalizedValue = this.normalizeValue(value)
            // 추가 검증: 정규화된 값이 유효한지 확인
            if (typeof normalizedValue === 'number' && !isNaN(normalizedValue)) {
              keywordGroups[keyword].push([timestamp, normalizedValue])
            }
          }
        })
      })

      // ApexChart 시리즈 형태로 변환
      const series = []
      Object.keys(keywordGroups).forEach(keyword => {
        series.push({
          name: keyword,
          data: keywordGroups[keyword] // 이미 날짜순으로 정렬됨
        })
      })

      return series
    },

    // 날짜 범위 생성 헬퍼 메서드
    generateDateRange() {
      const dates = []
      const startDate = new Date(this.startDate)
      const endDate = new Date(this.endDate)

      const currentDate = new Date(startDate)
      while (currentDate <= endDate) {
        dates.push(currentDate.toISOString().split('T')[0])
        currentDate.setDate(currentDate.getDate() + 1)
      }

      return dates
    },

    // 데이터 포인트 검증 및 정규화 헬퍼 메서드
    isValidDataPoint(timestamp) {
      // timestamp 검증
      if (!timestamp || isNaN(timestamp) || timestamp <= 0) {
        return false
      }

      // 날짜가 유효한 범위 내에 있는지 확인
      const date = new Date(timestamp)
      const startDate = new Date(this.startDate)
      const endDate = new Date(this.endDate)

      if (date < startDate || date > endDate) {
        return false
      }

      return true
    },

    // 값 정규화 (null, undefined, NaN을 0으로 변환)
    normalizeValue(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return 0
      }
      return Number(value)
    },

    // 추가 키워드 수집
    addKeywordToTrace() {
      if (!this.additionalKeyword.trim()) {
        this.$toast.error('키워드를 입력해주세요.')
        return
      }

      if (!this.trafficData || this.trafficData.length === 0) {
        this.$toast.error('먼저 상품 추적을 완료해주세요.')
        return
      }

      // 이미 존재하는 키워드인지 확인
      if (this.availableKeywords.includes(this.additionalKeyword.trim())) {
        this.$toast.error('이미 수집된 키워드입니다.')
        return
      }

      this.isAddingKeyword = true

      // Extension으로 추가 키워드 수집 요청
      window.postMessage({
        type: 'addKeywordToTrace',
        keyword: this.additionalKeyword.trim(),
        leafCategoryCode: this.productInfo?.leafCategoryCode,
        itemIds: this.productInfo?.itemIds,
        startDate: this.startDate,
        endDate: this.endDate
      }, '*')
    },

    // 추가 키워드 수집 응답 처리
    handleAddKeywordResponse(data) {
      console.log('🔍 [Frontend] handleAddKeywordResponse 받은 데이터:', data)
      this.isAddingKeyword = false

      if (!data) {
        console.error('🔍 [Frontend] data가 undefined입니다')
        this.$toast.error('키워드 수집 응답 데이터가 없습니다')
        return
      }

      if (data && Object.prototype.hasOwnProperty.call(data, 'success') && data.success === true) {
        const keyword = data.keyword || '알 수 없는 키워드'
        console.log('🔍 [Frontend] 키워드 수집 성공:', keyword)

        // 성공 메시지 표시
        alert(`키워드 "${keyword}" 수집 완료!`)

        // 새로운 키워드 데이터를 기존 데이터에 추가
        if (data.keywordData && data.keywordData.dailyData && data.keywordData.dailyData.length > 0) {
          console.log('🔍 [Frontend] 기존 trafficData 길이:', this.trafficData.length)
          console.log('🔍 [Frontend] 기존 키워드 목록:', this.availableKeywords)
          console.log('🔍 [Frontend] 새로운 dailyData:', data.keywordData.dailyData)

          // dailyData 배열의 각 항목에 키워드 정보 추가 (새로운 구조와 기존 구조 모두 지원)
          const newKeywordData = data.keywordData.dailyData.map(item => ({
            ...item,
            keyword: data.keyword, // 새로운 구조용
            keywordDetail: {       // 기존 구조용
              keyword: data.keyword
            }
          }))

          console.log('🔍 [Frontend] 변환된 newKeywordData:', newKeywordData)
          console.log('🔍 [Frontend] 첫 번째 변환된 항목 상세:', newKeywordData[0])

          // 기존 trafficData에 새로운 키워드 데이터 병합
          this.trafficData = [...this.trafficData, ...newKeywordData]

          // 키워드 목록 업데이트 (기존 방식과 동일하게 처리)
          this.extractAvailableKeywords()

          console.log('🔍 [Frontend] 병합 후 trafficData 길이:', this.trafficData.length)
          console.log('🔍 [Frontend] 병합 후 키워드 목록:', this.availableKeywords)
          console.log('🔍 [Frontend] 새로 추가된 키워드:', data.keyword)
          console.log('🔍 [Frontend] 병합 후 trafficData 샘플:', this.trafficData.slice(-3)) // 마지막 3개 (새로 추가된 데이터)

          // 차트 다시 생성 (렌더링 문제 해결)
          this.isChartsHidden = true // 차트들을 d-none으로 숨김

          this.$nextTick(() => {
            setTimeout(() => {
              this.hideAllCharts() // 차트 상태 초기화
              this.createCharts()  // 새로운 데이터로 차트 생성

              // 차트 생성 완료 후 다시 보여줌
              setTimeout(() => {
                this.isChartsHidden = false
              }, 100)
            }, 200)
          })

          // 입력 필드 초기화
          this.additionalKeyword = ''
        } else {
          console.log('🔍 [Frontend] 키워드 데이터가 없거나 형식이 잘못됨:', data)
        }
      } else {
        console.log('🔍 [Frontend] 키워드 수집 실패:', data)
        const keyword = data?.keyword || '알 수 없음'
        const error = data?.error || '알 수 없는 오류'

        alert(`키워드 "${keyword}" 수집 실패: ${error}`)
      }
    }
  }
}
</script>

<style scoped>
.progress {
  background-color: #e9ecef;
}

.progress-bar {
  background-color: #007bff;
  transition: width 0.3s ease;
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
}

.form-group label {
  font-weight: 500;
  margin-bottom: 0.5rem;
}
</style>

<style>
/* Custom tooltip styles for ApexCharts */
.custom-tooltip {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  min-width: 200px;
}

.tooltip-title {
  font-weight: 600;
  font-size: 14px;
  color: #333;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #f0f0f0;
}

.tooltip-item {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  font-size: 13px;
}

.tooltip-item:last-child {
  margin-bottom: 0;
}

.tooltip-marker {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
  flex-shrink: 0;
}

.tooltip-label {
  color: #666;
  margin-right: 6px;
  flex-shrink: 0;
}

.tooltip-value {
  font-weight: 600;
  color: #333;
  margin-left: auto;
}
</style>
